import { useRef } from 'react';
import { useState, useEffect } from 'react';
import { Star, Check, Trash2, Lock, LogOut, MapPin, Gift, Save, Package, Mail, Send, Copy, DollarSign, TrendingUp, Users, Tag, Plus, Wrench, ImagePlus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const StarDisplay = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(star => (
      <Star key={star} size={14} className={star <= rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-gray-600'} />
    ))}
  </div>
);

const promoStatus = (cfg) => {
  if (!cfg.enabled) return { label: 'DISABLED', cls: 'text-gray-400 bg-gray-500/10' };
  const today = new Date().toISOString().slice(0, 10);
  if (today < cfg.start_date) return { label: 'SCHEDULED', cls: 'text-[#D4AF37] bg-[#D4AF37]/10' };
  if (today > cfg.end_date) return { label: 'ENDED', cls: 'text-gray-400 bg-gray-500/10' };
  return { label: 'LIVE NOW', cls: 'text-green-500 bg-green-500/10' };
};

const inputCls = "w-full bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-3 py-2.5 rounded-sm outline-none transition-colors";

const MaintenanceManager = ({ storedPassword }) => {
  const [cfg, setCfg] = useState(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/maintenance`).then(res => {
      setCfg(res.data);
      setMessage(res.data.message || '');
    }).catch(() => toast.error('Failed to load maintenance status.'));
  }, []);

  const save = async (enabled) => {
    if (enabled && !window.confirm('This will take the website OFFLINE for all customers. Continue?')) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/maintenance?password=${storedPassword}`, { enabled, message });
      setCfg(res.data);
      setMessage(res.data.message || '');
      toast.success(enabled ? 'Site is now OFFLINE (maintenance mode on).' : 'Site is back ONLINE.');
    } catch {
      toast.error('Failed to update maintenance mode.');
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;

  return (
    <div className={`bg-[#0A0A0A] border rounded-sm p-6 mb-10 ${cfg.enabled ? 'border-[#C41E3A]' : 'border-[#27272A]'}`} data-testid="maintenance-manager">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white flex items-center gap-2">
          <Wrench size={18} className="text-[#D4AF37]" /> Maintenance Mode
        </h2>
        <span className={`text-xs uppercase tracking-wider px-3 py-1 rounded font-bold ${cfg.enabled ? 'text-[#C41E3A] bg-[#C41E3A]/15 animate-pulse' : 'text-green-500 bg-green-500/10'}`} data-testid="maintenance-status">
          {cfg.enabled ? 'SITE OFFLINE' : 'SITE ONLINE'}
        </span>
      </div>
      <p className="text-gray-500 text-xs mb-3">
        When on, customers see a "Down for Maintenance" page and checkout is blocked. This admin panel stays accessible.
      </p>
      <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1">Message shown to customers</label>
      <textarea
        value={message}
        onChange={e => setMessage(e.target.value)}
        rows={2}
        placeholder="We're temporarily down for maintenance. We'll be back shortly!"
        className={`${inputCls} resize-none mb-4`}
        data-testid="maintenance-message-input"
      />
      {cfg.enabled ? (
        <button
          onClick={() => save(false)}
          disabled={saving}
          className="w-full bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white font-bold uppercase tracking-wider py-3 rounded-sm transition-colors"
          data-testid="maintenance-off-btn"
        >
          {saving ? 'Saving...' : 'Turn Site Back ON'}
        </button>
      ) : (
        <button
          onClick={() => save(true)}
          disabled={saving}
          className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white font-bold uppercase tracking-wider py-3 rounded-sm transition-colors"
          data-testid="maintenance-on-btn"
        >
          {saving ? 'Saving...' : 'Take Site OFFLINE'}
        </button>
      )}
    </div>
  );
};

const StockManager = ({ storedPassword }) => {
  const [products, setProducts] = useState(null);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/stock?password=${storedPassword}`)
      .then(res => setProducts(res.data))
      .catch(() => toast.error('Failed to load stock list.'));
  }, [storedPassword]);

  const toggle = async (p) => {
    setSavingId(p.id);
    try {
      const res = await axios.put(`${API}/admin/stock?password=${storedPassword}`, { product_id: p.id, out_of_stock: !p.out_of_stock });
      setProducts(prev => prev.map(x => x.id === p.id ? { ...x, out_of_stock: res.data.out_of_stock } : x));
      toast.success(`${p.name} ${p.dosage} is now ${res.data.out_of_stock ? 'OUT OF STOCK' : 'IN STOCK'}${!res.data.out_of_stock ? ' — waitlist emails sending' : ''}`);
    } catch {
      toast.error('Failed to update stock.');
    } finally {
      setSavingId(null);
    }
  };

  const toggleSize = async (p, size) => {
    const soldOut = !(p.sold_out_sizes || []).includes(size);
    setSavingId(`${p.id}-${size}`);
    try {
      const res = await axios.put(`${API}/admin/stock/sizes?password=${storedPassword}`, { product_id: p.id, size, sold_out: soldOut });
      setProducts(prev => prev.map(x => x.id === p.id ? { ...x, sold_out_sizes: res.data.sold_out_sizes } : x));
      toast.success(`${p.name} size ${size} is now ${soldOut ? 'SOLD OUT' : 'IN STOCK'}`);
    } catch {
      toast.error('Failed to update size stock.');
    } finally {
      setSavingId(null);
    }
  };

  if (!products) return null;

  const q = search.trim().toLowerCase();
  const filtered = products.filter(p => !q || `${p.name} ${p.dosage} ${p.category}`.toLowerCase().includes(q));
  const outCount = products.filter(p => p.out_of_stock).length;

  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-6 mb-10" data-testid="stock-manager">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="stock-manager-toggle">
        <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white flex items-center gap-2">
          <Package size={18} className="text-[#D4AF37]" /> Stock Manager
        </h2>
        <span className={`text-xs uppercase tracking-wider px-3 py-1 rounded font-bold ${outCount > 0 ? 'text-[#C41E3A] bg-[#C41E3A]/15' : 'text-green-500 bg-green-500/10'}`} data-testid="stock-out-count">
          {outCount > 0 ? `${outCount} OUT OF STOCK` : 'ALL IN STOCK'}
        </span>
      </button>
      {open && (
        <div className="mt-4">
          <p className="text-gray-500 text-xs mb-3">
            Toggle any product in or out of stock instantly — no redeploy needed. Flipping a product back IN stock automatically emails everyone on its restock waitlist. For apparel, click a size chip to mark just that size sold out (red = sold out, green = in stock).
          </p>
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`${inputCls} mb-3`}
            data-testid="stock-search-input"
          />
          <div className="max-h-96 overflow-y-auto space-y-1 pr-1">
            {filtered.map(p => (
              <div key={p.id} className="bg-[#121212] border border-[#27272A] rounded-sm px-3 py-2" data-testid={`stock-row-${p.id}`}>
                <div className="flex items-center justify-between">
                <div className="min-w-0 mr-3">
                  <p className="text-white text-sm font-semibold truncate">{p.name} <span className="text-[#D4AF37] font-normal">{p.dosage}</span></p>
                  <p className="text-gray-600 text-[10px] uppercase tracking-wider">{p.category}</p>
                </div>
                <button
                  onClick={() => toggle(p)}
                  disabled={savingId === p.id}
                  className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors disabled:opacity-50 ${
                    p.out_of_stock
                      ? 'border-[#C41E3A] text-[#C41E3A] bg-[#C41E3A]/10 hover:bg-[#C41E3A]/20'
                      : 'border-green-600 text-green-500 bg-green-500/10 hover:bg-green-500/20'
                  }`}
                  data-testid={`stock-toggle-${p.id}`}
                >
                  {savingId === p.id ? '...' : p.out_of_stock ? 'Out of Stock' : 'In Stock'}
                </button>
                </div>
                {p.sizes.length > 0 && (
                  <div className="flex items-center flex-wrap gap-1.5 mt-2 pt-2 border-t border-[#1a1a1a]" data-testid={`size-stock-${p.id}`}>
                    <span className="text-gray-600 text-[9px] uppercase tracking-wider mr-1">Sizes:</span>
                    {p.sizes.map(size => {
                      const soldOut = (p.sold_out_sizes || []).includes(size);
                      return (
                        <button
                          key={size}
                          onClick={() => toggleSize(p, size)}
                          disabled={savingId === `${p.id}-${size}`}
                          title={soldOut ? `${size} sold out — click to restock` : `${size} in stock — click to mark sold out`}
                          className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors disabled:opacity-50 ${
                            soldOut
                              ? 'border-[#C41E3A]/60 text-[#C41E3A] bg-[#C41E3A]/10 line-through'
                              : 'border-green-700/60 text-green-500 bg-green-500/5'
                          }`}
                          data-testid={`size-stock-toggle-${p.id}-${size}`}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            {filtered.length === 0 && <p className="text-gray-500 text-xs text-center py-4">No products match your search.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const PromoManager = ({ storedPassword }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/promo-config?password=${storedPassword}`)
      .then(res => setCfg(res.data))
      .catch(() => toast.error('Failed to load promo settings.'));
  }, [storedPassword]);

  if (!cfg) return null;

  const status = promoStatus(cfg);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/promo-image?password=${storedPassword}`, fd);
      setCfg({ ...cfg, free_item_image: res.data.url });
      toast.success('Image updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeletePromo = async () => {
    if (!window.confirm('Delete this promo? The popup and code will stop working immediately, and the form will be cleared so you can set up a new one.')) return;
    try {
      const res = await axios.delete(`${API}/admin/promo-config?password=${storedPassword}`);
      const { status: _s, ...cleared } = res.data;
      setCfg(cleared);
      toast.success('Promo deleted. Fill in the form and save to launch a new one.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete promo.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/promo-config?password=${storedPassword}`, cfg);
      toast.success('Promo settings saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save promo settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-10" data-testid="promo-manager">
      <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-[#C41E3A] mb-4 flex items-center gap-2">
        <Gift size={18} /> Free Item Promo
      </h2>
      <div className="bg-[#0A0A0A] border border-[#C41E3A]/40 rounded-sm p-5">
        <div className="flex items-center justify-between mb-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={cfg.enabled}
              onChange={e => setCfg({ ...cfg, enabled: e.target.checked })}
              className="w-5 h-5 accent-[#C41E3A] cursor-pointer"
              data-testid="promo-enabled-toggle"
            />
            <span className="text-white text-sm font-semibold uppercase tracking-wider">Promo Enabled</span>
          </label>
          <span className={`text-xs uppercase tracking-wider px-2 py-1 rounded font-bold ${status.cls}`} data-testid="promo-status-badge">
            {status.label}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1.5">Promo Code</label>
            <input
              type="text"
              value={cfg.code}
              onChange={e => setCfg({ ...cfg, code: e.target.value.toUpperCase() })}
              className={`${inputCls} uppercase tracking-widest font-bold`}
              data-testid="promo-code-field"
            />
          </div>
          <div>
            <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1.5">Minimum Peptide Spend ($)</label>
            <input
              type="number"
              min="1"
              value={cfg.min_spend}
              onChange={e => setCfg({ ...cfg, min_spend: parseFloat(e.target.value) || 0 })}
              className={inputCls}
              data-testid="promo-min-spend-field"
            />
          </div>
          <div>
            <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1.5">Start Date</label>
            <input
              type="date"
              value={cfg.start_date}
              onChange={e => setCfg({ ...cfg, start_date: e.target.value })}
              className={inputCls}
              style={{ colorScheme: 'dark' }}
              data-testid="promo-start-date-field"
            />
          </div>
          <div>
            <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1.5">End Date (last day, inclusive)</label>
            <input
              type="date"
              value={cfg.end_date}
              onChange={e => setCfg({ ...cfg, end_date: e.target.value })}
              className={inputCls}
              style={{ colorScheme: 'dark' }}
              data-testid="promo-end-date-field"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1.5">Free Item (not from the store list — this is the promo giveaway)</label>
          <div className="flex gap-3 items-start">
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div className="w-20 h-20 bg-white rounded-sm overflow-hidden border border-[#27272A]">
                {cfg.free_item_image ? (
                  <img src={cfg.free_item_image} alt={cfg.free_item_name || 'Promo item'} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-[9px] text-center px-1">No image</div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleImageUpload} className="hidden" data-testid="promo-image-input" />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="text-[#D4AF37] hover:text-white text-[10px] uppercase tracking-wider transition-colors disabled:opacity-50"
                data-testid="promo-change-image-btn"
              >
                {uploading ? 'Uploading...' : 'Change Image'}
              </button>
            </div>
            <div className="flex-1 space-y-2">
              <input
                type="text"
                value={cfg.free_item_name}
                onChange={e => setCfg({ ...cfg, free_item_name: e.target.value })}
                placeholder="Item name (e.g. PEPTIDE CASE)"
                className={inputCls}
                data-testid="promo-free-item-name"
              />
              <input
                type="text"
                value={cfg.free_item_dosage}
                onChange={e => setCfg({ ...cfg, free_item_dosage: e.target.value })}
                placeholder="Details (e.g. 7 SLOTS • WITH LOGO)"
                className={inputCls}
                data-testid="promo-free-item-details"
              />
            </div>
          </div>
        </div>

        <p className="text-gray-500 text-xs mb-4">
          Customers who spend ${Math.round(cfg.min_spend)}+ on peptides between {cfg.start_date} and {cfg.end_date} can enter code <span className="text-[#D4AF37] font-bold">{cfg.code}</span> at checkout to get a free {cfg.free_item_name} ({cfg.free_item_dosage}). The popup and cart progress bar turn on and off automatically with these dates — no redeploy needed.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white text-sm font-bold uppercase tracking-wider px-6 py-2.5 rounded-sm transition-all duration-300"
            data-testid="promo-save-btn"
          >
            <Save size={15} /> {saving ? 'Saving...' : 'Save Promo Settings'}
          </button>
          <button
            onClick={handleDeletePromo}
            className="flex items-center gap-2 border border-[#C41E3A]/60 text-[#C41E3A] hover:bg-[#C41E3A] hover:text-white text-sm font-bold uppercase tracking-wider px-6 py-2.5 rounded-sm transition-all duration-300"
            data-testid="promo-delete-btn"
          >
            <Trash2 size={15} /> Delete Promo
          </button>
        </div>
      </div>
    </div>
  );
};

const OrderLog = ({ storedPassword }) => {
  const [orders, setOrders] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/orders?password=${storedPassword}`)
      .then(res => setOrders(res.data))
      .catch(() => toast.error('Failed to load orders.'));
  }, [storedPassword]);

  const deleteOrder = async (num) => {
    if (!window.confirm(`Delete order ${num}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/orders/${num}?password=${storedPassword}`);
      setOrders(prev => prev.filter(o => o.order_number !== num));
      toast.success(`Order ${num} deleted.`);
    } catch {
      toast.error('Failed to delete order.');
    }
  };

  if (!orders) return null;

  const visible = showAll ? orders : orders.slice(0, 10);
  const freeCaseCount = orders.filter(o => o.free_item).length;

  return (
    <div className="mb-10" data-testid="order-log">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-[#D4AF37] flex items-center gap-2">
          <Package size={18} /> Order Log ({orders.length})
        </h2>
        {freeCaseCount > 0 && (
          <span className="text-green-400 text-xs uppercase tracking-wider bg-green-500/10 px-2 py-1 rounded" data-testid="free-case-count">
            {freeCaseCount} free case{freeCaseCount !== 1 ? 's' : ''} given away
          </span>
        )}
      </div>
      {orders.length === 0 ? (
        <p className="text-gray-500 text-sm bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4">No orders logged yet. Orders placed from now on will appear here.</p>
      ) : (
        <>
          <div className="space-y-3">
            {visible.map(o => (
              <div key={o.order_number} className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4" data-testid={`order-${o.order_number}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-[#C41E3A] font-mono font-bold tracking-wider">{o.order_number}</span>
                    <span className="text-gray-500 text-xs">
                      {new Date(o.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {o.promo_code && (
                      <span className="text-[#D4AF37] text-[10px] uppercase tracking-wider bg-[#D4AF37]/10 px-2 py-0.5 rounded font-bold">{o.promo_code}</span>
                    )}
                    {o.free_item && (
                      <span className="text-green-400 text-[10px] uppercase tracking-wider bg-green-500/10 px-2 py-0.5 rounded font-bold">Free Case</span>
                    )}
                    <span className="text-white font-bold">${o.total.toFixed(2)}</span>
                    <button
                      onClick={() => deleteOrder(o.order_number)}
                      className="p-1.5 text-gray-600 hover:text-[#C41E3A] transition-colors"
                      title="Delete order"
                      data-testid={`delete-order-${o.order_number}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <p className="text-gray-300 text-sm">
                  {o.customer_name}
                  {o.city && <span className="text-gray-500"> — {o.city}{o.province ? `, ${o.province}` : ''}</span>}
                  {o.customer_email && <span className="text-gray-500 text-xs ml-2">{o.customer_email}</span>}
                  {o.phone && <span className="text-[#D4AF37] text-xs ml-2" data-testid="order-phone">📞 {o.phone}</span>}
                  {o.shipping_method === 'pickup' && <span className="text-green-400 text-[10px] uppercase tracking-wider bg-green-500/10 px-2 py-0.5 rounded font-bold ml-2">Pickup — Airdrie</span>}
                  {o.shipping_method === 'express_on' && <span className="text-[#D4AF37] text-[10px] uppercase tracking-wider bg-[#D4AF37]/10 px-2 py-0.5 rounded font-bold ml-2">ON Express $30</span>}
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  {o.items.map(i => `${i.qty}× ${i.name}`).join(', ')}
                </p>
              </div>
            ))}
          </div>
          {orders.length > 10 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="mt-3 text-gray-400 hover:text-white text-xs uppercase tracking-wider transition-colors"
              data-testid="order-log-toggle"
            >
              {showAll ? 'Show less' : `Show all ${orders.length} orders`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

const SubscribersManager = ({ storedPassword }) => {
  const [subs, setSubs] = useState(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showList, setShowList] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImg, setUploadingImg] = useState(false);
  const [testEmail, setTestEmail] = useState('canadianwellness2026@outlook.com');
  const [sendingTest, setSendingTest] = useState(false);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState('');
  const imgInputRef = useRef(null);

  const load = () => {
    axios.get(`${API}/admin/subscribers?password=${storedPassword}`)
      .then(res => setSubs(res.data))
      .catch(() => toast.error('Failed to load subscribers.'));
    axios.get(`${API}/peptides`).then(res => setProducts(res.data)).catch(() => {});
  };

  useEffect(load, [storedPassword]);

  if (!subs) return null;

  const handleSend = async () => {
    if (!window.confirm(`Send this to all ${subs.length} subscribers?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/newsletter?password=${storedPassword}`, { subject, message, image_url: imageUrl, product_id: productId });
      toast.success(`Sending to ${res.data.count} subscriber${res.data.count !== 1 ? 's' : ''}!`);
      setSubject('');
      setMessage('');
      setImageUrl('');
      setProductId('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send.');
    } finally {
      setSending(false);
    }
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/admin/newsletter-image?password=${storedPassword}`, form);
      setImageUrl(res.data.url);
      toast.success('Image attached.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image.');
    } finally {
      setUploadingImg(false);
      if (imgInputRef.current) imgInputRef.current.value = '';
    }
  };

  const handleTest = async () => {
    setSendingTest(true);
    try {
      await axios.post(`${API}/admin/newsletter-test?password=${storedPassword}&test_email=${encodeURIComponent(testEmail)}`, { subject, message, image_url: imageUrl, product_id: productId });
      toast.success(`Test email sent to ${testEmail} — check your inbox!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email.');
    } finally {
      setSendingTest(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(subs.map(s => s.email).join(', '));
    toast.success('All emails copied to clipboard.');
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Remove ${email} from the list?`)) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${encodeURIComponent(email)}?password=${storedPassword}`);
      setSubs(subs.filter(s => s.email !== email));
      toast.success('Subscriber removed.');
    } catch {
      toast.error('Failed to remove subscriber.');
    }
  };

  return (
    <div className="mb-10" data-testid="subscribers-manager">
      <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-[#D4AF37] mb-4 flex items-center gap-2">
        <Mail size={18} /> Subscribers ({subs.length})
      </h2>
      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5">
        <h3 className="text-white text-sm font-semibold uppercase tracking-wider mb-3">Send an Update</h3>
        <input
          type="text"
          value={subject}
          onChange={e => setSubject(e.target.value)}
          placeholder="Subject (e.g. New Products Just Landed!)"
          className={`${inputCls} mb-3`}
          data-testid="newsletter-subject"
        />
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Your message... (deals, new products, promo announcements)"
          rows={5}
          className={`${inputCls} mb-3 resize-y`}
          data-testid="newsletter-message"
        />
        {/* Image attachment */}
        <input ref={imgInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleImagePick} className="hidden" data-testid="newsletter-image-input" />
        {imageUrl ? (
          <div className="flex items-center gap-4 bg-[#121212] border border-[#27272A] rounded-sm p-3 mb-3" data-testid="newsletter-image-preview">
            <img src={`${API.replace('/api', '')}${imageUrl}`} alt="Attached" className="w-20 h-20 object-cover rounded-sm border border-[#27272A]" />
            <div className="flex-1">
              <p className="text-white text-xs font-semibold uppercase tracking-wider">Image attached</p>
              <p className="text-gray-500 text-xs mt-0.5">It will appear at the top of the email.</p>
            </div>
            <button
              onClick={() => setImageUrl('')}
              className="text-gray-400 hover:text-[#C41E3A] transition-colors"
              data-testid="newsletter-image-remove"
              aria-label="Remove image"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => imgInputRef.current?.click()}
            disabled={uploadingImg}
            className="flex items-center gap-2 border border-[#27272A] hover:border-[#D4AF37] text-gray-400 hover:text-[#D4AF37] text-xs uppercase tracking-wider px-4 py-2.5 rounded-sm mb-3 transition-colors disabled:opacity-50"
            data-testid="newsletter-attach-btn"
          >
            <ImagePlus size={14} /> {uploadingImg ? 'Uploading...' : 'Attach a Picture (optional)'}
          </button>
        )}
        {/* Feature a product */}
        <div className="mb-3" data-testid="newsletter-product-row">
          <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1">Feature a product with a "Shop Now" button (optional)</label>
          <select
            value={productId}
            onChange={e => setProductId(e.target.value)}
            className={`${inputCls} cursor-pointer`}
            data-testid="newsletter-product-select"
          >
            <option value="">No featured product</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>{p.name} — {p.dosage} — {p.price}</option>
            ))}
          </select>
        </div>
        {/* Send test to myself */}
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="newsletter-test-row">
          <input
            type="email"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="you@email.com"
            className={`${inputCls} !w-auto flex-1 min-w-[220px]`}
            data-testid="newsletter-test-email"
          />
          <button
            onClick={handleTest}
            disabled={sendingTest || !subject.trim() || !message.trim() || !testEmail.trim()}
            className="flex items-center gap-2 border border-[#D4AF37]/60 hover:border-[#D4AF37] text-[#D4AF37] text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors disabled:opacity-40"
            data-testid="newsletter-test-btn"
          >
            <Send size={13} /> {sendingTest ? 'Sending...' : 'Send Test to Myself'}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleSend}
            disabled={sending || !subject.trim() || !message.trim() || subs.length === 0}
            className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white text-sm font-bold uppercase tracking-wider px-6 py-2.5 rounded-sm transition-all duration-300"
            data-testid="newsletter-send-btn"
          >
            <Send size={15} /> {sending ? 'Sending...' : `Send to ${subs.length} Subscriber${subs.length !== 1 ? 's' : ''}`}
          </button>
          {subs.length > 0 && (
            <>
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 text-gray-400 hover:text-white text-xs uppercase tracking-wider transition-colors"
                data-testid="copy-emails-btn"
              >
                <Copy size={13} /> Copy all emails
              </button>
              <button
                onClick={() => setShowList(!showList)}
                className="text-gray-400 hover:text-white text-xs uppercase tracking-wider transition-colors"
                data-testid="toggle-subscriber-list"
              >
                {showList ? 'Hide list' : 'Show list'}
              </button>
            </>
          )}
        </div>
        {subs.length === 0 && (
          <p className="text-gray-500 text-xs mt-3">No subscribers yet — signup boxes are on the homepage, footer, and checkout.</p>
        )}
        {showList && (
          <div className="mt-4 space-y-1.5 max-h-64 overflow-y-auto">
            {subs.map(s => (
              <div key={s.email} className="flex items-center justify-between bg-[#121212] border border-[#27272A] rounded-sm px-3 py-2">
                <div>
                  <span className="text-gray-300 text-sm">{s.email}</span>
                  <span className="text-gray-600 text-[10px] uppercase ml-2">{s.source}</span>
                  {s.subscribed_at && (
                    <span className="text-gray-600 text-[10px] ml-2" data-testid={`sub-date-${s.email}`}>
                      {new Date(s.subscribed_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </div>
                <button onClick={() => handleDelete(s.email)} className="text-gray-600 hover:text-[#C41E3A] transition-colors" aria-label={`Remove ${s.email}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const MembersManager = ({ storedPassword }) => {
  const [data, setData] = useState(null);
  const [showList, setShowList] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/members?password=${storedPassword}`)
      .then(res => setData(res.data))
      .catch(() => {});
  }, [storedPassword]);

  if (!data) return null;

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '—';
  const cards = [
    { label: 'Total Members', value: data.total_members, sub: `${data.new_this_week} new this week` },
    { label: 'Active This Week', value: data.active_this_week, sub: 'opened the tracker' },
    { label: 'Injections Logged', value: data.total_injections_logged, sub: `${data.total_protocols} protocols` },
    { label: 'Tracking Extras', value: data.total_vials + data.total_progress_entries + data.total_photos, sub: `${data.total_vials} vials · ${data.total_progress_entries} progress · ${data.total_photos} photos` },
  ];

  return (
    <div className="mb-10" data-testid="members-manager">
      <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-[#D4AF37] mb-4 flex items-center gap-2">
        <Users size={18} /> Member App ({data.total_members})
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        {cards.map(c => (
          <div key={c.label} className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4" data-testid={`member-stat-${c.label.toLowerCase().replace(/ /g, '-')}`}>
            <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">{c.label}</p>
            <p className="text-white font-['Oswald'] text-2xl">{c.value}</p>
            <p className="text-gray-500 text-xs">{c.sub}</p>
          </div>
        ))}
      </div>
      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4">
        <button onClick={() => setShowList(!showList)} className="text-[#D4AF37] hover:text-white text-xs font-bold uppercase tracking-wider transition-colors" data-testid="members-toggle-list">
          {showList ? 'Hide member list' : `Show member list (${data.members.length})`}
        </button>
        {showList && (
          <div className="mt-3 divide-y divide-[#1a1a1a]" data-testid="members-list">
            <div className="hidden sm:grid grid-cols-5 gap-2 pb-2 text-gray-600 text-[10px] uppercase tracking-wider">
              <span className="col-span-2">Member</span><span>Joined</span><span>Last Active</span><span className="text-right">Usage</span>
            </div>
            {data.members.map(m => (
              <div key={m.email} className="grid sm:grid-cols-5 gap-1 sm:gap-2 py-2 text-sm items-center">
                <span className="sm:col-span-2 text-white truncate">{m.first_name ? `${m.first_name} — ` : ''}{m.email}</span>
                <span className="text-gray-400 text-xs">{fmt(m.created_at)}</span>
                <span className={`text-xs ${m.last_seen ? 'text-green-400' : 'text-gray-600'}`}>{fmt(m.last_seen)}</span>
                <span className="text-gray-400 text-xs sm:text-right">{m.injections} inj · {m.protocols} proto</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StatsBar = ({ storedPassword }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/stats?password=${storedPassword}`)
      .then(res => setStats(res.data))
      .catch(() => {});
  }, [storedPassword]);

  if (!stats) return null;

  const cards = [
    { icon: DollarSign, label: 'Total Revenue', value: `$${stats.total_revenue.toFixed(2)}`, sub: `$${stats.revenue_this_month.toFixed(2)} this month` },
    { icon: Package, label: 'Orders', value: stats.total_orders, sub: `${stats.orders_this_month} this month` },
    { icon: TrendingUp, label: 'Avg Order', value: `$${stats.avg_order_value.toFixed(2)}`, sub: 'per order' },
    { icon: Users, label: 'Subscribers', value: stats.subscribers, sub: 'on your list' },
  ];

  return (
    <div className="mb-10" data-testid="stats-bar">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        {cards.map(c => (
          <div key={c.label} className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4">
            <div className="flex items-center gap-2 text-gray-500 text-[10px] uppercase tracking-wider mb-1">
              <c.icon size={12} className="text-[#C41E3A]" /> {c.label}
            </div>
            <p className="text-white font-['Oswald'] text-2xl">{c.value}</p>
            <p className="text-gray-500 text-xs">{c.sub}</p>
          </div>
        ))}
      </div>
      {stats.top_products.length > 0 && (
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4">
          <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-2">Top Sellers</p>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            {stats.top_products.map((p, i) => (
              <span key={p.name} className="text-sm">
                <span className="text-[#D4AF37] font-bold">{i + 1}.</span>{' '}
                <span className="text-gray-300">{p.name}</span>{' '}
                <span className="text-gray-500 text-xs">×{p.qty}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      <p className="text-gray-600 text-[10px] mt-1.5">Stats cover orders since the order log began (older orders exist only in email).</p>
    </div>
  );
};

const fmtMonth = (m) => new Date(m + '-02').toLocaleDateString('en-CA', { month: 'short', year: '2-digit' });

const SalesTracker = ({ storedPassword }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/sales-tracker?password=${storedPassword}`)
      .then(res => setData(res.data))
      .catch(() => {});
  }, [storedPassword]);

  if (!data || data.products.length === 0) return null;

  const q = search.trim().toLowerCase();
  const rows = data.products.filter(p => !q || p.name.toLowerCase().includes(q));
  const monthTotals = data.months.map(m => data.products.reduce((s, p) => s + (p.monthly[m] || 0), 0));

  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-6 mb-10" data-testid="sales-tracker">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="sales-tracker-toggle">
        <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white flex items-center gap-2">
          <TrendingUp size={18} className="text-[#D4AF37]" /> Monthly Sales Tracker
        </h2>
        <span className="text-xs uppercase tracking-wider px-3 py-1 rounded font-bold text-[#D4AF37] bg-[#D4AF37]/10">
          {data.products.length} products · {data.months.length} months
        </span>
      </button>
      {open && (
        <div className="mt-4">
          <p className="text-gray-500 text-xs mb-3">
            Units sold per product per month — includes FREE promo items since they use up stock. Use this to plan reorders.
          </p>
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`${inputCls} mb-3`}
            data-testid="sales-tracker-search"
          />
          <div className="overflow-x-auto max-h-[28rem] overflow-y-auto border border-[#27272A] rounded-sm">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#121212] z-10">
                <tr>
                  <th className="text-left text-gray-400 text-[10px] uppercase tracking-wider px-3 py-2 sticky left-0 bg-[#121212] min-w-[200px]">Product</th>
                  {data.months.map(m => (
                    <th key={m} className="text-center text-gray-400 text-[10px] uppercase tracking-wider px-3 py-2 whitespace-nowrap">{fmtMonth(m)}</th>
                  ))}
                  <th className="text-center text-[#D4AF37] text-[10px] uppercase tracking-wider px-3 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(p => (
                  <tr key={p.name} className="border-t border-[#1a1a1a] hover:bg-[#121212]" data-testid={`sales-row-${p.name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}>
                    <td className="px-3 py-2 sticky left-0 bg-[#0A0A0A] min-w-[200px]">
                      <span className="text-gray-200 text-xs">{p.name}</span>
                      {p.free && <span className="ml-1.5 text-green-500 text-[9px] font-bold uppercase">Free</span>}
                    </td>
                    {data.months.map(m => (
                      <td key={m} className={`text-center px-3 py-2 text-xs ${p.monthly[m] ? 'text-white font-semibold' : 'text-gray-700'}`}>
                        {p.monthly[m] || '—'}
                      </td>
                    ))}
                    <td className="text-center px-3 py-2 text-[#D4AF37] font-bold text-xs">{p.total}</td>
                  </tr>
                ))}
                <tr className="border-t border-[#27272A] bg-[#121212]">
                  <td className="px-3 py-2 sticky left-0 bg-[#121212] text-gray-400 text-[10px] uppercase tracking-wider font-bold">All units</td>
                  {monthTotals.map((t, idx) => (
                    <td key={data.months[idx]} className="text-center px-3 py-2 text-[#D4AF37] font-bold text-xs">{t}</td>
                  ))}
                  <td className="text-center px-3 py-2 text-[#D4AF37] font-bold text-xs">{monthTotals.reduce((a, b) => a + b, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {rows.length === 0 && <p className="text-gray-500 text-xs text-center py-4">No products match your search.</p>}
        </div>
      )}
    </div>
  );
};

const ReferralCodesManager = ({ storedPassword }) => {
  const [codes, setCodes] = useState(null);
  const [newCode, setNewCode] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [eventActive, setEventActive] = useState(null);
  const [eventBusy, setEventBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/referral-event`).then(res => setEventActive(res.data.active)).catch(() => {});
  }, []);

  const toggleEvent = async () => {
    const next = !eventActive;
    if (!window.confirm(next
      ? 'Restart the 2-for-1 Point Event? New referrals will earn 2 points again starting today.'
      : 'End the 2-for-1 Point Event? Points already earned during the event are kept, but new referrals go back to 1 point each.')) return;
    setEventBusy(true);
    try {
      const res = await axios.post(`${API}/admin/referral-event?password=${storedPassword}`, { active: next });
      setEventActive(res.data.active);
      toast.success(`2-for-1 Point Event is now ${res.data.active ? 'ON' : 'OFF'}`);
    } catch {
      toast.error('Failed to update the event.');
    } finally {
      setEventBusy(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/admin/referral-codes?password=${storedPassword}`)
      .then(res => setCodes(res.data))
      .catch(() => toast.error('Failed to load referral codes.'));
  }, [storedPassword]);

  if (!codes) return null;

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const res = await axios.post(`${API}/admin/referral-codes?password=${storedPassword}`, {
        code: newCode, vip_email: newEmail
      });
      setCodes([...codes, { ...res.data, uses: 0 }].sort((a, b) => a.code.localeCompare(b.code)));
      setNewCode('');
      setNewEmail('');
      toast.success(`${res.data.code} added!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add code.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete referral code ${code}? Customers will no longer be able to use it.`)) return;
    try {
      await axios.delete(`${API}/admin/referral-codes/${code}?password=${storedPassword}`);
      setCodes(codes.filter(c => c.code !== code));
      toast.success(`${code} deleted.`);
    } catch {
      toast.error('Failed to delete code.');
    }
  };

  return (
    <div className="mb-10" data-testid="referral-codes-manager">
      <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-[#D4AF37] mb-4 flex items-center gap-2">
        <Tag size={18} /> Referral Codes ({codes.length})
      </h2>
      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5">
        <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-2 mb-4">
          <input
            type="text"
            required
            value={newCode}
            onChange={e => setNewCode(e.target.value.toUpperCase())}
            placeholder="NEW CODE (e.g. SARAH10)"
            className={`${inputCls} sm:w-48 uppercase tracking-widest font-bold`}
            data-testid="new-referral-code-input"
          />
          <input
            type="email"
            required
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="VIP email (the referrer)"
            className={`${inputCls} flex-1`}
            data-testid="new-referral-email-input"
          />
          <button
            type="submit"
            disabled={adding}
            className="flex items-center justify-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm transition-all duration-300 whitespace-nowrap"
            data-testid="add-referral-code-btn"
          >
            <Plus size={14} /> {adding ? 'Adding...' : 'Add Code'}
          </button>
        </form>
        <p className="text-gray-500 text-xs mb-4">
          New codes get the standard rules: 10% off peptides, one-time per customer email. The VIP email gets unlimited use and upgrades to 20% at 100 points. <span className="text-[#C41E3A]">Codes with no orders for 60 days are deleted automatically — you'll get an email listing any deletions.</span>
        </p>
        {eventActive !== null && (
          <div className={`flex items-center justify-between border rounded-sm px-4 py-3 mb-4 ${eventActive ? 'border-[#C41E3A]/60 bg-[#C41E3A]/5' : 'border-[#27272A] bg-[#121212]'}`} data-testid="referral-event-toggle-row">
            <p className="text-xs">
              <span className={`font-bold uppercase tracking-wider ${eventActive ? 'text-[#C41E3A]' : 'text-gray-500'}`}>
                2-for-1 Point Event: {eventActive ? 'ON' : 'OFF'}
              </span>
              <span className="text-gray-500 ml-2">{eventActive ? 'Every referral earns 2 points.' : 'Referrals earn 1 point each.'}</span>
            </p>
            <button
              onClick={toggleEvent}
              disabled={eventBusy}
              className={`text-[10px] font-bold uppercase tracking-wider px-4 py-2 rounded-sm border transition-colors disabled:opacity-50 ${
                eventActive
                  ? 'border-[#C41E3A] text-[#C41E3A] hover:bg-[#C41E3A]/15'
                  : 'border-green-600 text-green-500 hover:bg-green-500/15'
              }`}
              data-testid="referral-event-toggle-btn"
            >
              {eventBusy ? '...' : eventActive ? 'End Event' : 'Restart Event'}
            </button>
          </div>
        )}
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {codes.map(c => (
            <div key={c.code} className="flex items-center justify-between bg-[#121212] border border-[#27272A] rounded-sm px-3 py-2" data-testid={`referral-code-${c.code}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-[#D4AF37] font-bold tracking-wider whitespace-nowrap">{c.code}</span>
                <span className="text-gray-500 text-xs truncate">{(c.vip_emails || [])[0]}</span>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {typeof c.days_inactive === 'number' && (
                  <span
                    className={`text-[10px] font-bold whitespace-nowrap ${c.days_inactive >= 50 ? 'text-[#C41E3A]' : c.days_inactive >= 40 ? 'text-[#D4AF37]' : 'text-gray-600'}`}
                    title="Days since last order with this code"
                    data-testid={`code-idle-${c.code}`}
                  >
                    {c.days_inactive}d idle
                  </span>
                )}
                <span className={`text-xs ${c.uses > 0 ? 'text-green-400' : 'text-gray-600'}`}>
                  {c.uses} use{c.uses !== 1 ? 's' : ''}
                </span>
                <span className={`text-xs font-bold ${c.points >= 100 ? 'text-[#C41E3A]' : 'text-[#D4AF37]'}`} data-testid={`code-points-${c.code}`}>
                  {c.points || 0}/100 pts
                </span>
                <button onClick={() => handleDelete(c.code)} className="text-gray-600 hover:text-[#C41E3A] transition-colors" aria-label={`Delete ${c.code}`} data-testid={`delete-code-${c.code}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const AdminPage = () => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [storedPassword, setStoredPassword] = useState('');

  useEffect(() => {
    const manifest = document.querySelector('link[rel="manifest"]');
    if (manifest) manifest.remove();
    return () => {
      if (manifest && !document.querySelector('link[rel="manifest"]')) document.head.appendChild(manifest);
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password });
      setAuthenticated(true);
      setStoredPassword(password);
      fetchReviews(password);
    } catch {
      toast.error('Invalid password.');
    }
  };

  const fetchReviews = async (pw) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reviews?password=${pw}`);
      setReviews(res.data);
    } catch {
      toast.error('Failed to load reviews.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      await axios.put(`${API}/admin/reviews/${reviewId}/approve?password=${storedPassword}`);
      toast.success('Review approved!');
      fetchReviews(storedPassword);
    } catch {
      toast.error('Failed to approve review.');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}?password=${storedPassword}`);
      toast.success('Review deleted.');
      fetchReviews(storedPassword);
    } catch {
      toast.error('Failed to delete review.');
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="admin-login">
        <form onSubmit={handleLogin} className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-8 max-w-sm w-full">
          <div className="text-center mb-6">
            <Lock size={32} className="text-[#D4AF37] mx-auto mb-3" />
            <h1 className="font-['Oswald'] text-2xl uppercase tracking-wide text-white">Admin Panel</h1>
          </div>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter password"
            className="w-full bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors mb-4"
            data-testid="admin-password"
          />
          <button
            type="submit"
            className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white font-bold uppercase tracking-wider py-3 rounded-sm transition-all duration-300"
            data-testid="admin-login-btn"
          >
            Log In
          </button>
        </form>
      </div>
    );
  }

  const pending = reviews.filter(r => !r.approved);
  const approved = reviews.filter(r => r.approved);

  return (
    <div className="min-h-screen bg-[#050505] py-12" data-testid="admin-panel">
      <meta name="robots" content="noindex" />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-['Oswald'] text-3xl uppercase tracking-wide text-white">Admin Panel</h1>
          <button
            onClick={() => { setAuthenticated(false); setStoredPassword(''); setPassword(''); }}
            className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>

        <MaintenanceManager storedPassword={storedPassword} />

        <StockManager storedPassword={storedPassword} />

        <StatsBar storedPassword={storedPassword} />

        <SalesTracker storedPassword={storedPassword} />

        <PromoManager storedPassword={storedPassword} />

        <ReferralCodesManager storedPassword={storedPassword} />

        <SubscribersManager storedPassword={storedPassword} />

        <MembersManager storedPassword={storedPassword} />

        <OrderLog storedPassword={storedPassword} />

        <h2 className="font-['Oswald'] text-2xl uppercase tracking-wide text-white mb-6">Review Manager</h2>

        {loading ? (
          <p className="text-gray-400 text-center">Loading reviews...</p>
        ) : (
          <>
            {/* Pending Reviews */}
            <div className="mb-10">
              <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-[#D4AF37] mb-4">
                Pending Approval ({pending.length})
              </h2>
              {pending.length === 0 ? (
                <p className="text-gray-500 text-sm bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4">No pending reviews.</p>
              ) : (
                <div className="space-y-4">
                  {pending.map(r => (
                    <div key={r.review_id} className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-sm p-5" data-testid={`pending-review-${r.review_id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <p className="text-white font-semibold">{r.name}</p>
                            {r.location && <p className="text-gray-500 text-xs flex items-center gap-1"><MapPin size={10} />{r.location}</p>}
                          </div>
                          {r.email && <p className="text-gray-400 text-xs mb-1">{r.email}</p>}
                          <StarDisplay rating={r.rating} />
                        </div>
                        <div className="text-right">
                          <span className="text-[#D4AF37] text-xs uppercase tracking-wider bg-[#D4AF37]/10 px-2 py-1 rounded">Pending</span>
                          {r.promo_code && <p className="text-gray-500 text-[10px] mt-1">Code: {r.promo_code}</p>}
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm mb-4 leading-relaxed">"{r.review}"</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(r.review_id)}
                          className="flex items-center gap-1.5 bg-green-700 hover:bg-green-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-sm transition-colors"
                          data-testid={`approve-${r.review_id}`}
                        >
                          <Check size={14} /> Approve
                        </button>
                        <button
                          onClick={() => handleDelete(r.review_id)}
                          className="flex items-center gap-1.5 bg-red-800 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-sm transition-colors"
                          data-testid={`delete-${r.review_id}`}
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Approved Reviews */}
            <div>
              <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-green-500 mb-4">
                Approved ({approved.length})
              </h2>
              {approved.length === 0 ? (
                <p className="text-gray-500 text-sm bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4">No approved reviews yet.</p>
              ) : (
                <div className="space-y-4">
                  {approved.map(r => (
                    <div key={r.review_id} className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5" data-testid={`approved-review-${r.review_id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <p className="text-white font-semibold">{r.name}</p>
                            {r.location && <p className="text-gray-500 text-xs flex items-center gap-1"><MapPin size={10} />{r.location}</p>}
                          </div>
                          <StarDisplay rating={r.rating} />
                        </div>
                        <span className="text-green-500 text-xs uppercase tracking-wider bg-green-500/10 px-2 py-1 rounded">Live</span>
                      </div>
                      <p className="text-gray-300 text-sm mb-4 leading-relaxed">"{r.review}"</p>
                      <button
                        onClick={() => handleDelete(r.review_id)}
                        className="flex items-center gap-1.5 text-gray-500 hover:text-red-500 text-xs uppercase tracking-wider transition-colors"
                        data-testid={`delete-approved-${r.review_id}`}
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
